/**
 * Learning Center API: the learner's own "me" reads that Home is built from
 * (plan docs/plans/lms-plan.md 7.1, 9.4, Appendix A; 18.16, 18.18, 18.24;
 * WP-41). Typed calls over services/api (the shared axios instance that adds
 * the JWT and X-Site-Id); nothing under src/lms imports axios (2.3a).
 *
 *   GET /me/assignments?status=&limit=&offset=  -> { items, page } (WP-31 mine.js):
 *        open ones by due date (the to-do order of 7.1), each with the course
 *        summary, source_label (why it was assigned, in words), due_state and
 *        change_summary (18.16, 18.27)
 *   GET /me/summary                              -> the 9.4 counts (WP-33; 18.18)
 *   GET /me/onboarding                           -> { run, items, progress } | null (WP-38; 18.24):
 *        the caller's active run in this network, for Home's onboarding card
 *
 * The learner's catalog, bookmark, attempt and completion calls are WP-40's
 * (api/courses.ts, api/progress.ts, api/attempts.ts, api/completions.ts;
 * plan 18.22) and are imported from there, never duplicated here.
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type { LmsList } from './client';
import type { MyAssignment } from './courses';

// ---------------------------------------------------------------------------
// GET /me/assignments (Appendix A; WP-31 mine.js)
// ---------------------------------------------------------------------------

/** The course summary each assignment carries (WP-31 mine.js). */
export interface MyAssignmentCourse {
  id: string;
  title: string;
  slug: string;
  category: string;
  kind: string;
  validity_months: number | null;
  current_version_id: string | null;
  /** Minutes of the pinned version, else of the current one; null when neither is known. */
  estimated_minutes: number | null;
}

/**
 * One GET /me/assignments item: the player's fields (api/courses.ts
 * MyAssignment) plus the course summary and the close columns.
 */
export interface MyAssignmentItem extends MyAssignment {
  course: MyAssignmentCourse | null;
  source_ref: string | null;
  created_at: string | null;
  completed_at: string | null;
  completion_id: string | null;
  waived_at: string | null;
  waive_reason: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
}

export type MyAssignmentStatus = 'open' | 'completed' | 'waived' | 'cancelled';

/** GET /api/lms/me/assignments (Appendix A: limit at most 200). */
export async function listMyAssignments(
  options: { status?: MyAssignmentStatus | null; limit?: number; offset?: number } = {}
): Promise<LmsList<MyAssignmentItem>> {
  const params: Record<string, string | number> = {};
  if (options.status) params.status = options.status;
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<MyAssignmentItem>>('/api/lms/me/assignments', { params });
  return response.data;
}

// ---------------------------------------------------------------------------
// GET /me/summary (9.4; 18.18)
// ---------------------------------------------------------------------------

/**
 * 9.4: overdue = open assignments past due_at; due_within_7 = open
 * assignments due within 7 days; onboarding_pending_items counts every
 * pending item of the active run in the network (18.18).
 */
export interface MySummary {
  open: number;
  overdue: number;
  due_within_7: number;
  unread_notifications: number;
  onboarding_active: boolean;
  onboarding_pending_items: number;
}

/** GET /api/lms/me/summary (WP-48 polls it every 60 s from LmsProvider, D14). */
export async function getMySummary(): Promise<MySummary> {
  const response = await api.get<MySummary>('/api/lms/me/summary');
  return response.data;
}

// ---------------------------------------------------------------------------
// GET /me/onboarding (12.3; 18.24): the fields Home's onboarding card reads
// ---------------------------------------------------------------------------

/** 18.24: progress = { total, done, not_applicable, pending, awaiting_verification, overdue }. */
export interface MyOnboardingProgress {
  total: number;
  done: number;
  not_applicable: number;
  pending: number;
  awaiting_verification: number;
  overdue: number;
}

/** The run row (5.7 lms_onboarding_runs), the fields the card shows. */
export interface MyOnboardingRun {
  id: string;
  template_id?: string;
  site_id?: string;
  start_date: string;
  status: 'active' | 'completed' | 'cancelled';
  started_at?: string;
}

/** One run item as the card counts it (the Onboarding tab, WP-47, reads the full row). */
export interface MyOnboardingItemSummary {
  id: string;
  title: string;
  status: 'pending' | 'done' | 'not_applicable';
  due_on: string;
  responsible: 'new_hire' | 'admin' | string;
}

export interface MyOnboarding {
  run: MyOnboardingRun;
  items: MyOnboardingItemSummary[];
  progress: MyOnboardingProgress;
}

/** GET /api/lms/me/onboarding: the active run in this network, or null (18.24). */
export async function getMyOnboarding(): Promise<MyOnboarding | null> {
  const response = await api.get<MyOnboarding | null>('/api/lms/me/onboarding');
  const data = response.data;
  return data && typeof data === 'object' && data.run ? data : null;
}
